import { useEffect, useRef } from 'react';
import { useClerkInstanceAs } from '@/hooks/useClerkGate';
import i18n, { isSupportedLanguage, type SupportedLanguage } from '@/i18n/config';
import { logWarning } from '@/lib/logger';

const METADATA_KEY = 'language';

// Minimal structural view of the clerk instance — same approach as useClerkGate:
// we read/write through the stable instance (useClerkInstanceAs) rather than the
// reactive `useUser()` hook (banned project-wide; it re-renders on every ~50s
// token refresh → UI-freeze class).
interface ClerkUserLike {
  id: string;
  unsafeMetadata?: Record<string, unknown>;
  update: (params: { unsafeMetadata: Record<string, unknown> }) => Promise<unknown>;
}
interface ClerkInstanceLike {
  user?: ClerkUserLike | null;
  addListener: (listener: (...args: unknown[]) => void) => () => void;
}

function writeMetadata(
  user: ClerkUserLike,
  language: SupportedLanguage,
  context: string
): void {
  void user
    .update({
      unsafeMetadata: { ...user.unsafeMetadata, [METADATA_KEY]: language },
    })
    .catch((error: unknown) => {
      logWarning(context, {
        module: 'UserLanguageSync',
        error: error instanceof Error ? error.message : String(error),
      });
    });
}

/**
 * Persists the user's language preference to their Clerk profile so it survives
 * across devices/browsers (where localStorage `aica-language` isn't available).
 *
 * Mounted as an isolated null-rendering leaf (sibling of RouterProvider in
 * main.tsx), only under ClerkProvider — never in embedded (Bitrix24) mode, and
 * never inside a layout, so its updates don't cascade into the router tree.
 *
 * Precedence on first reconcile for a given user:
 *   1. explicit `?lang=` in the URL — a deliberate this-visit choice (e.g. the
 *      marketing-site handoff) wins over stale cross-device metadata, and the
 *      profile is updated to match.
 *   2. profile metadata — the cross-device preference (e.g. a fresh device).
 *   3. neither — backfill the profile with the current language (captures the
 *      language the user signed up in).
 * Manual toggles are persisted by the second effect.
 */
export function UserLanguageSync() {
  const clerk = useClerkInstanceAs<ClerkInstanceLike>();
  // Keyed by user id, not a boolean, so an account switch within the same SPA
  // session re-applies the new user's preference instead of keeping the first
  // user's language.
  const appliedForUserRef = useRef<string | null>(null);

  useEffect(() => {
    const reconcile = () => {
      const user = clerk.user;
      if (!user || appliedForUserRef.current === user.id) return;
      appliedForUserRef.current = user.id;

      const urlLang = new URLSearchParams(window.location.search).get('lang');
      const stored = user.unsafeMetadata?.[METADATA_KEY];

      // 1. Explicit ?lang handoff — honor it and update the profile to match.
      if (isSupportedLanguage(urlLang)) {
        if (urlLang !== i18n.language) void i18n.changeLanguage(urlLang);
        if (stored !== urlLang) {
          writeMetadata(user, urlLang, 'Failed to update language in Clerk metadata');
        }
        return;
      }

      // 2. No explicit signal — apply the cross-device profile preference.
      if (isSupportedLanguage(stored)) {
        if (stored !== i18n.language) void i18n.changeLanguage(stored);
        return;
      }

      // 3. No preference anywhere — capture the current (sign-up) language.
      if (isSupportedLanguage(i18n.language)) {
        writeMetadata(
          user,
          i18n.language,
          'Failed to backfill language to Clerk metadata'
        );
      }
    };

    // addListener invokes the listener immediately with the current state and
    // replays at hydration (see useClerkGate NOTE), so reconcile runs on mount
    // for an already-loaded user and again whenever the user changes.
    return clerk.addListener(reconcile);
  }, [clerk]);

  // Persist manual language switches for signed-in users.
  useEffect(() => {
    const onChange = (lng: string) => {
      const user = clerk.user;
      if (!user || !isSupportedLanguage(lng)) return;
      if (user.unsafeMetadata?.[METADATA_KEY] === lng) return;
      writeMetadata(user, lng, 'Failed to persist language to Clerk metadata');
    };

    i18n.on('languageChanged', onChange);
    return () => i18n.off('languageChanged', onChange);
  }, [clerk]);

  return null;
}
